import React, { useEffect, useState } from "react";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { Link, useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  Col,
  Input,
  InputGroup,
  Row,
} from "reactstrap";

import PurchaseService from "../../services/OrderingServices/PurchaseService";
import PurchasesServices from "../../services/Inventory Services/PurchasesServices";
import { Triangle } from "react-loader-spinner";
import CustomDataTable from "../../Components/CustomTable/CustomTable";
import DeleteModal from "../../Components/Common/DeleteModal";
import { formatDateTimes } from "../../Components/Common/FormatDate";
import toast from "react-hot-toast";

const customStyles = {
  headCells: {
    style: {
      fontSize: "0.92rem",
      fontWeight: 610,
    },
  },
  rows: {
    style: {
      fontSize: "0.9rem",
    },
  },
};

const StatusBadge = ({ status, type }) => {
  let cls = "bg-secondary";
  if (type === "status") {
    cls =
      status?.toLowerCase() === "pending"
        ? "bg-warning"
        : status?.toLowerCase() === "completed"
          ? "bg-success"
          : status?.toLowerCase() === "cancelled"
            ? "bg-danger"
            : "bg-secondary";
  } else if (type === "payment") {
    cls =
      status?.toLowerCase() === "pending"
        ? "bg-warning"
        : status?.toLowerCase() === "paid"
          ? "bg-success"
          : status?.toLowerCase() === "failed"
            ? "bg-danger"
            : status?.toLowerCase() === "refunded"
              ? "bg-info"
              : "bg-light";
  }
  return <span className={`badge ${cls}`}>{status}</span>;
};

const LoadingSpinner = ({ size = 80 }) => (
  <div className="my-3 text-center d-flex justify-content-center">
    <Triangle visible={true} height={size} width={size} color="#5B71B9" />
    <h5 className="mt-1">Loading...</h5>
  </div>
);

const Purchase = () => {
  const [purchaseList, setPurchaseList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [filterText, setFilterText] = useState("");
  const navigate = useNavigate();

  const fetchPurchase = async () => {
    try {
      setLoading(true);
      const response = await PurchaseService.getList();
      setPurchaseList(response);
    } catch (error) {
      console.log("🚀 ~ error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPurchase();
  }, []);

  const handleFilterChange = (e) => setFilterText(e.target.value);

  const filteredData = purchaseList?.filter((pkg) => {
    const searchContext = `${pkg?.supplierName ?? ""} ${
      pkg?.paymentStatus ?? ""
    } ${pkg?.supplierContact ?? ""}`.toLowerCase();
    return searchContext.includes(filterText.toLowerCase());
  });

  const toggleDeleteModal = () => setDeleteModal(!deleteModal);
  const handleDeleteClick = (id) => {
    setSelectedItem(id);
    toggleDeleteModal();
  };
  const handleDelete = async () => {
    if (selectedItem) {
      try {
        await PurchasesServices.delete(selectedItem);
        setPurchaseList((prev) =>
          prev.filter((pkg) => pkg.id !== selectedItem)
        );
        toast.success("Selected purchase deleted successfully");
      } catch (error) {
        toast.error("Failed to delete the record");
      } finally {
        toggleDeleteModal();
        setSelectedItem(null);
      }
    }
  };

  const handleMarkPaid = async (id) => {
    try {
      const response = await PurchaseService.markBillPaid(id);
      toast.success("Purchase marked as paid successfully");

      setPurchaseList((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, paymentStatus: "Paid" } : item
        )
      );
    } catch (error) {
      toast.error("Failed to mark as paid");
      console.error("Error making padi: ", error);
    }
  };

  const columns = [
    {
      name: "S.N",
      selector: (_, index) => index + 1,
      sortable: true,
      width: "75px",
    },
    {
      name: "Date",
      selector: (row) => (
        <span
          title={formatDateTimes(row.purchaseDate)}
          className="cursor-pointer"
        >
          {formatDateTimes(row.purchaseDate)}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Supplier",
      selector: (row) => (
        <span title={row.supplierName}>{row.supplierName}</span>
      ),
      sortable: true,
    },
    { name: "Contact", selector: (row) => row.supplierContact, sortable: true },
    {
      name: "Status",
      selector: (row) => <StatusBadge status={row.status} type="status" />,
      sortable: true,
    },
    {
      name: "Payment Status",
      selector: (row) => (
        <StatusBadge status={row.paymentStatus} type="payment" />
      ),
      sortable: true,
    },
    {
      name: "Total Amt",
      selector: (row) => row.totalAmount,
      sortable: true,
      right: true,
    },
    {
      name: "Action",
      selector: (row) => (
        <div className="d-flex gap-1">
          <Link to={`/purchase/detail/${row.id}`}>
            <Button color="btn btn-soft-success" size="sm">
              <i className="bx bx-show"></i>
            </Button>
          </Link>
          <Button
            color="btn btn-soft-warning"
            size="sm"
            onClick={() => navigate(`/purchase/update/${row.id}`)}
            title="Update Purchase"
          >
            <i className="ri-edit-line"></i>
          </Button>
          <Button
            className="btn btn-soft-danger"
            size="sm"
            onClick={() => handleDeleteClick(row.id)}
          >
            <i className="ri-delete-bin-5-line"></i>
          </Button>
        </div>
      ),
      center: true,
    },
  ];

  return (
    <div className="page-content">
      <div className="container-fluid">
        <BreadCrumb title="Purchase List" pageTitle="Purchase" />

        {/* Desktop Search + Create */}
        <div className="d-none d-md-block d-lg-block mb-2">
          <Row>
            <Col md={8}>
              <InputGroup>
                <Input
                  placeholder="Search by Supplier Name, Contact and Payment Status"
                  value={filterText}
                  onChange={handleFilterChange}
                />
              </InputGroup>
            </Col>
            <Col md={4} className="d-flex justify-content-end">
              <Link to="/purchase/create">
                <Button color="success">+ Create purchase</Button>
              </Link>
            </Col>
          </Row>
          <Card className="mt-2">
            <CardBody>
              <CustomDataTable
                responsive
                striped
                pagination
                fixedHeader
                persistTableHead
                progressPending={loading}
                customStyles={customStyles}
                progressComponent={<LoadingSpinner />}
                columns={columns}
                data={filteredData}
              />
            </CardBody>
          </Card>
        </div>

        {/* Mobile Search + Create */}
        <div className="d-block d-md-none d-lg-none mb-2">
          <Row className="mb-2">
            <Col md={8} xs={12} className="mb-2">
              <InputGroup>
                <Input
                  placeholder="Search by org name, email or org code"
                  value={filterText}
                  onChange={handleFilterChange}
                />
              </InputGroup>
            </Col>
            <Col
              md={4}
              xs={12}
              className="d-flex justify-content-end align-items-end"
            >
              <Link to="/purchase/create">
                <Button color="success">+ Create purchase</Button>
              </Link>
            </Col>
          </Row>

          {loading ? (
            <LoadingSpinner size={60} />
          ) : filteredData.length > 0 ? (
            filteredData.map((item, index) => (
              <Card key={item.id} className="mb-3 shadow-sm border-1">
                <CardBody>
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <h6 className="mb-0">Purchase #{index + 1}</h6>
                    <div className="d-flex gap-1">
                      <Link to={`/purchase/detail/${item.id}`}>
                        <Button color="success" size="sm">
                          <i className="bx bx-show"></i>
                        </Button>
                      </Link>
                      <Button
                        color="warning"
                        size="sm"
                        onClick={() => navigate(`/purchase/update/${item.id}`)}
                      >
                        <i className="ri-edit-line"></i>
                      </Button>
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() => handleDeleteClick(item.id)}
                      >
                        <i className="ri-delete-bin-5-line"></i>
                      </Button>
                    </div>
                  </div>

                  <Row className="mb-1">
                    <Col xs={5} className="text-muted fw-medium">
                      Date:
                    </Col>
                    <Col xs={7}>{formatDateTimes(item.purchaseDate)}</Col>
                  </Row>
                  <Row className="mb-1">
                    <Col xs={5} className="text-muted fw-medium">
                      Supplier:
                    </Col>
                    <Col xs={7}>{item.supplierName}</Col>
                  </Row>
                  <Row className="mb-1">
                    <Col xs={5} className="text-muted fw-medium">
                      Contact:
                    </Col>
                    <Col xs={7}>{item.supplierContact}</Col>
                  </Row>
                  <Row className="mb-1">
                    <Col xs={5} className="text-muted fw-medium">
                      Status:
                    </Col>
                    <Col xs={7}>
                      <StatusBadge status={item.status} type="status" />
                    </Col>
                  </Row>
                  <Row className="mb-1">
                    <Col xs={5} className="text-muted fw-medium">
                      Payment:
                    </Col>
                    <Col xs={7}>
                      <StatusBadge status={item.paymentStatus} type="payment" />
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={5} className="text-muted fw-medium">
                      Total Amt:
                    </Col>
                    <Col xs={7}>{item.totalAmount}</Col>
                  </Row>
                </CardBody>
              </Card>
            ))
          ) : (
            <div className="text-center py-3">
              <p className="text-muted mb-0">No purchase records found.</p>
            </div>
          )}
        </div>

        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDelete}
          onCloseClick={toggleDeleteModal}
          selectedItem={selectedItem}
        />
      </div>
    </div>
  );
};

export default Purchase;
